if (!customElements.get("details-modal")) {
  class DetailsModal extends HTMLElement {
    static registry = new Map();
    static documentListenersReady = false;
    static defaultOpenTrigger = "data-details-modal-open";

    static initDocumentListeners() {
      if (DetailsModal.documentListenersReady) return;
      DetailsModal.documentListenersReady = true;

      const handleTrigger = (event) => {
        const match = DetailsModal._findTriggerMatch(event.target);
        if (!match) return;

        event.preventDefault();
        match.instance.open();
      };

      document.addEventListener("click", handleTrigger);
      document.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        handleTrigger(event);
      });
    }

    static _findTriggerMatch(target) {
      for (const instance of DetailsModal.registry.values()) {
        const trigger = target.closest(`[${instance._openTrigger}]`);
        if (!trigger) continue;

        const modalId = trigger.getAttribute(instance._openTrigger) || trigger.getAttribute("modal-id");

        if (!modalId || modalId !== instance._modalId) continue;

        return { instance, trigger };
      }

      return null;
    }

    constructor() {
      super();
      this._isClosing = false;
      this._onDialogClick = this._handleDialogClick.bind(this);
      this._onDialogClose = this._handleDialogClose.bind(this);
      this._onCloseButtonClick = this._handleCloseButtonClick.bind(this);
    }

    connectedCallback() {
      DetailsModal.initDocumentListeners();

      this._dialog = this.querySelector("dialog");
      this._modalId = this.getAttribute("modal-id") || this.id;
      this._openTrigger = this.getAttribute("open-trigger") || DetailsModal.defaultOpenTrigger;
      this._lockClass = this.getAttribute("lock-class") || "details-modal-open";
      this._transitionDuration = Number(this.getAttribute("transition-duration")) || 350;

      if (!this._dialog || !this._modalId) return;

      if (!this._dialog.id) {
        this._dialog.id = this._modalId;
      }

      DetailsModal.registry.set(this._modalId, this);

      this._closeButtons = this._dialog.querySelectorAll("[data-details-modal-close]");
      this._closeButtons.forEach((button) => {
        button.addEventListener("click", this._onCloseButtonClick);
      });

      this._dialog.addEventListener("click", this._onDialogClick);
      this._dialog.addEventListener("close", this._onDialogClose);
    }

    disconnectedCallback() {
      if (this._modalId) {
        DetailsModal.registry.delete(this._modalId);
      }

      if (this._closeButtons) {
        this._closeButtons.forEach((button) => {
          button.removeEventListener("click", this._onCloseButtonClick);
        });
      }

      if (this._dialog) {
        this._dialog.removeEventListener("click", this._onDialogClick);
        this._dialog.removeEventListener("close", this._onDialogClose);
      }

      this._removeLockClass();
    }

    open() {
      if (!this._dialog || this._dialog.open) return;

      if (typeof this._dialog.showModal === "function") {
        this._dialog.showModal();
      } else {
        this._dialog.setAttribute("open", "");
      }

      document.documentElement.classList.add(this._lockClass);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._dialog.classList.add("is-visible");
        });
      });
    }

    close() {
      if (!this._dialog || this._isClosing) return;

      this._isClosing = true;
      this._dialog.classList.remove("is-visible");

      const finishClose = () => {
        if (!this._isClosing) return;

        this._isClosing = false;

        if (typeof this._dialog.close === "function") {
          this._dialog.close();
        } else {
          this._dialog.removeAttribute("open");
        }

        this._removeLockClass();
      };

      this._dialog.addEventListener("transitionend", finishClose, { once: true });
      window.setTimeout(finishClose, this._transitionDuration);
    }

    _handleCloseButtonClick() {
      this.close();
    }

    _handleDialogClick(event) {
      if (event.target === this._dialog) {
        this.close();
      }
    }

    _handleDialogClose() {
      this._dialog.classList.remove("is-visible");
      this._removeLockClass();
      this._isClosing = false;
    }

    _removeLockClass() {
      if (this._lockClass) {
        document.documentElement.classList.remove(this._lockClass);
      }
    }
  }

  customElements.define("details-modal", DetailsModal);
}
